import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, LogOut, RefreshCw, Send, Phone, MapPin, Printer, PackageCheck, Undo2, Search, Copy, MessageSquare, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "@/components/Header";
import { PlatePreview } from "@/components/PlatePreview";
import { api, errorMessage, formatEur } from "@/lib/api";

const TOKEN_KEY = "rpm_admin_token";
const COLOR_HEX = { white: "#FFFFFF", blue: "#2563EB", pink: "#EC4899", red: "#EF4444" };
const TABS = [
  { id: "new", label: "Новые" },
  { id: "printed", label: "Напечатаны" },
  { id: "shipped", label: "Отправлены" },
  { id: "all", label: "Все оплаченные" },
  { id: "unpaid", label: "Не оплачены" },
];
const BADGE = {
  new: { label: "Новый", cls: "bg-[var(--rpm-orange)] text-black" },
  printed: { label: "Напечатан", cls: "bg-[var(--rpm-lime)] text-black" },
  shipped: { label: "Отправлен", cls: "bg-black text-white" },
  unpaid: { label: "Не оплачен", cls: "bg-zinc-200 text-zinc-700" },
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

const authApi = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

function Login({ onLogin }) {
  const [step, setStep] = useState("start");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async () => {
    setBusy(true);
    try {
      await api.post("/admin/login/request");
      setStep("code");
      toast.success("Код отправлен в Telegram");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (code.trim().length < 4) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login/verify", { code: code.trim() });
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rpm-grid-bg flex min-h-screen items-center justify-center px-4 py-10">
      <div className="rpm-card w-full max-w-md p-7 sm:p-9" data-testid="admin-login-card">
        <Logo />
        <h1 className="mt-8 font-display text-2xl font-bold tracking-tight">Заказы · вход для владельца</h1>
        <p className="mt-2 text-sm text-zinc-600">Мы отправим одноразовый код в ваш Telegram-бот @Rpm3dprintorder_bot.</p>

        {step === "start" ? (
          <>
            <button onClick={request} disabled={busy} className="rpm-btn rpm-btn-orange mt-6 h-14 w-full text-base" data-testid="admin-request-code-button">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />} Получить код в Telegram
            </button>
            <button onClick={() => setStep("code")} className="mt-3 w-full text-center text-sm text-zinc-600 underline-offset-4 hover:underline" data-testid="admin-have-code-button">
              У меня уже есть код
            </button>
          </>
        ) : (
          <form onSubmit={verify} className="mt-6">
            <label htmlFor="code" className="mb-1.5 block text-sm font-medium">Код из Telegram</label>
            <input
              id="code"
              autoFocus
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="000000"
              className="rpm-input w-full px-4 text-center font-mono-rpm text-2xl tracking-[0.4em]"
              data-testid="admin-code-input"
            />
            <button type="submit" disabled={busy || code.trim().length < 4} className="rpm-btn rpm-btn-lime mt-4 h-14 w-full text-base" data-testid="admin-verify-button">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <KeyRound className="h-5 w-5" />} Войти
            </button>
            <button type="button" onClick={request} disabled={busy} className="mt-3 w-full text-center text-sm text-zinc-600 underline-offset-4 hover:underline" data-testid="admin-resend-code-button">
              Отправить код ещё раз
            </button>
          </form>
        )}
        <a href="/" className="mt-8 block text-center text-xs text-zinc-500 hover:text-black">← На сайт</a>
      </div>
    </div>
  );
}

const copy = (text) => {
  navigator.clipboard?.writeText(text).then(
    () => toast.success("Скопировано"),
    () => toast.error("Не удалось скопировать")
  );
};

function OrderCard({ o, onStatus, busy }) {
  const status = o.status !== "paid" ? "unpaid" : o.fulfillment_status || "new";
  const b = BADGE[status];
  const tg = (o.customer?.telegram || "").replace(/^@/, "").trim();

  return (
    <article className="rpm-card overflow-hidden" data-testid={`admin-order-${o.order_number}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-200 px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="font-display text-lg font-bold">{o.order_number}</span>
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${b.cls}`} data-testid={`admin-order-${o.order_number}-status`}>
            {b.label}
          </span>
        </div>
        <span className="font-mono-rpm text-xs text-zinc-500">{fmtDate(o.paid_at || o.created_at)}</span>
      </div>

      <div className="grid gap-5 p-5 md:grid-cols-[1.4fr_1fr]">
        <ul className="space-y-4">
          {o.items.map((it, i) => (
            <li key={i} className="flex gap-4">
              <div className="w-32 shrink-0">
                <PlatePreview type={it.product_id === "invisible" ? "invisible" : "individual"} top={it.top_text || ""} bottom={it.bottom_text} color={COLOR_HEX[it.text_color]} className="w-full" />
              </div>
              <div className="min-w-0 flex-1 text-sm">
                <div className="font-display font-semibold">
                  {it.name} <span className="text-[var(--rpm-orange)]">× {it.quantity}</span>
                </div>
                {it.product_id === "invisible" && <div className="mt-1 text-xs text-zinc-600">в наборе 2 комплекта: для больших и для маленьких номеров (каждый на 2 номера)</div>}
                {it.text_color && (
                  <div className="mt-1 space-y-1">
                    {[["Сверху", it.top_text], ["Снизу", it.bottom_text]].filter(([label, val]) => label === "Снизу" || val).map(([label, val]) => (
                      <div key={label} className="flex items-center gap-2">
                        <span className="w-14 shrink-0 text-xs text-zinc-500">{label}</span>
                        <span className="truncate rounded bg-zinc-100 px-1.5 py-0.5 font-mono-rpm text-xs text-black">{val || "—"}</span>
                        {val && (
                          <button onClick={() => copy(val)} className="text-zinc-400 hover:text-black" aria-label="Копировать">
                            <Copy className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                    <div className="flex items-center gap-2">
                      <span className="w-14 shrink-0 text-xs text-zinc-500">Цвет</span>
                      <span className="inline-block h-3.5 w-3.5 rounded-full border border-black" style={{ background: COLOR_HEX[it.text_color] }} />
                      <span className="text-xs font-medium">{it.text_color_label}</span>
                    </div>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>

        <div className="space-y-2.5 rounded-xl bg-zinc-50 p-4 text-sm">
          <div className="font-display font-semibold">{o.customer?.name}</div>
          <a href={`tel:${(o.customer?.phone || "").replace(/\s/g, "")}`} className="flex items-center gap-2 hover:underline" data-testid={`admin-order-${o.order_number}-phone`}>
            <Phone className="h-4 w-4" /> {o.customer?.phone}
          </a>
          {tg && (
            <a href={`https://t.me/${tg}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:underline">
              <Send className="h-4 w-4" /> @{tg}
            </a>
          )}
          <div className="flex items-start gap-2">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              <span className="font-medium">{o.delivery?.label}</span>
              {o.delivery?.locker && <span className="block text-xs text-zinc-600">{o.delivery.locker}</span>}
            </span>
          </div>
          {o.comment && (
            <div className="flex items-start gap-2">
              <MessageSquare className="mt-0.5 h-4 w-4 shrink-0" />
              <span className="text-zinc-700">{o.comment}</span>
            </div>
          )}
          {o.discount > 0 && (
            <div className="flex justify-between text-xs font-medium text-[var(--rpm-orange)]">
              <span>Скидка за пары</span>
              <span>−{formatEur(o.discount)}</span>
            </div>
          )}
          <div className="flex items-end justify-between border-t border-zinc-200 pt-2.5">
            <span className="text-zinc-600">Итого</span>
            <span className="font-display text-xl font-bold">{formatEur(o.total)}</span>
          </div>
        </div>
      </div>

      {status !== "unpaid" && (
        <div className="flex flex-col gap-2 border-t border-zinc-200 px-5 py-4 sm:flex-row sm:justify-end">
          {status === "new" && (
            <button disabled={busy} onClick={() => onStatus(o, "printed")} className="rpm-btn rpm-btn-lime" data-testid={`admin-order-${o.order_number}-mark-printed`}>
              <Printer className="h-4 w-4" /> Напечатан
            </button>
          )}
          {status !== "shipped" && (
            <button disabled={busy} onClick={() => onStatus(o, "shipped")} className="rpm-btn rpm-btn-orange" data-testid={`admin-order-${o.order_number}-mark-shipped`}>
              <PackageCheck className="h-4 w-4" /> Отправлен
            </button>
          )}
          {status !== "new" && (
            <button disabled={busy} onClick={() => onStatus(o, status === "shipped" ? "printed" : "new")} className="rpm-btn rpm-btn-outline" data-testid={`admin-order-${o.order_number}-undo`}>
              <Undo2 className="h-4 w-4" /> {status === "shipped" ? "Вернуть в «Напечатан»" : "Вернуть в «Новые»"}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

function StripeHealthCard({ health }) {
  const live = health?.payment_mode === "live";
  const charges = !!health?.charges_enabled;
  const details = !!health?.details_submitted;
  const payouts = !!health?.payouts_enabled;
  const ready = live && charges && payouts;

  const tone = ready
    ? "border-black bg-[var(--rpm-lime)]"
    : charges
      ? "border-black bg-[var(--rpm-yellow)]"
      : "border-black bg-[var(--rpm-yellow)]";

  const title = ready
    ? "✅ Stripe в боевом режиме — реальные платежи принимаются"
    : charges
      ? "⚠️ Stripe принимает платежи, но режим не Live — проверьте публикацию"
      : "🧪 Оплата в тестовом режиме — настоящие деньги пока не приходят";

  const Dot = ({ ok, label }) => (
    <div className="flex items-center gap-2 text-sm">
      <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full border border-black text-[11px] font-bold ${ok ? "bg-[var(--rpm-lime)]" : "bg-white"}`}>
        {ok ? "✓" : "·"}
      </span>
      <span>{label}</span>
    </div>
  );

  return (
    <div className={`mt-5 rounded-2xl border-[1.5px] p-4 sm:p-5 ${tone}`} data-testid="admin-stripe-health-card">
      <div className="font-display text-base font-bold" data-testid="admin-stripe-health-title">{title}</div>

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="rounded-xl border border-black/15 bg-white/70 p-3">
          <div className="text-xs uppercase tracking-wider text-black/60">Состояние аккаунта Stripe</div>
          <div className="mt-2 flex flex-col gap-1.5">
            <Dot ok={details} label="Данные бизнеса отправлены (KYC)" />
            <Dot ok={charges} label="Приём платежей включён (charges_enabled)" />
            <Dot ok={payouts} label="Выплаты на карту включены (payouts_enabled)" />
            <Dot ok={live} label='Приложение запущено в режиме "Live"' />
          </div>
        </div>
        <div className="rounded-xl border border-black/15 bg-white/70 p-3">
          <div className="text-xs uppercase tracking-wider text-black/60">Параметры</div>
          <dl className="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-black/60">Режим</dt>
            <dd className="font-mono" data-testid="admin-stripe-mode">{health.payment_mode}{health.stripe_mode_env ? ` (env: ${health.stripe_mode_env})` : ""}</dd>
            <dt className="text-black/60">Страна</dt>
            <dd className="font-mono">{health.country || "—"}</dd>
            <dt className="text-black/60">Валюта</dt>
            <dd className="font-mono uppercase">{health.default_currency || "—"}</dd>
            <dt className="text-black/60">Account ID</dt>
            <dd className="truncate font-mono text-xs">{health.account_id || "—"}</dd>
            <dt className="text-black/60">Webhook secret</dt>
            <dd className="font-mono text-xs">{health.has_webhook_secret ? "задан" : "нет"}</dd>
          </dl>
        </div>
      </div>

      {!ready && (
        <p className="mt-3 text-sm leading-relaxed text-black">
          <b>Что сделать:</b> в Emergent открой <b>Manage → Payments</b>, нажми <b>Claim</b> и завершите онбординг в Stripe
          (данные бизнеса, IBAN для выплат). Затем снова опубликуй приложение (<b>Publish</b>) — платформа автоматически подставит
          боевые ключи, и этот баннер станет зелёным.
        </p>
      )}
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [tab, setTab] = useState("new");
  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState("");

  const handleErr = useCallback(
    (e) => {
      if (e?.response?.status === 401) {
        toast.error("Сессия истекла, войдите снова");
        onLogout();
      } else toast.error(errorMessage(e));
    },
    [onLogout]
  );

  const load = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);
      try {
        const [o, s, h] = await Promise.all([
          api.get(`/admin/orders?status=${tab}`, authApi(token)),
          api.get("/admin/stats", authApi(token)),
          api.get("/admin/stripe/health", authApi(token)).catch(() => ({ data: null })),
        ]);
        setOrders(o.data.orders);
        setStats(s.data);
        setHealth(h.data);
      } catch (e) {
        handleErr(e);
      } finally {
        setLoading(false);
      }
    },
    [tab, token, handleErr]
  );

  useEffect(() => {
    load();
    const id = setInterval(() => load(true), 60000);
    return () => clearInterval(id);
  }, [load]);

  const setStatus = async (o, s) => {
    setBusy(o.order_number);
    try {
      await api.patch(`/admin/orders/${o.order_number}`, { fulfillment_status: s }, authApi(token));
      toast.success(`${o.order_number}: ${BADGE[s].label.toLowerCase()}`);
      await load(true);
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  };

  const logout = async () => {
    try {
      await api.post("/admin/logout", {}, authApi(token));
    } catch {
      /* ignore */
    }
    onLogout();
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return orders;
    return orders.filter((o) =>
      [o.order_number, o.customer?.name, o.customer?.phone, o.customer?.telegram, o.delivery?.locker, ...o.items.flatMap((i) => [i.top_text, i.bottom_text])]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(s))
    );
  }, [orders, q]);

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="sticky top-0 z-30 border-b border-zinc-200 bg-white">
        <div className="mx-auto flex h-[68px] max-w-6xl items-center justify-between gap-3 px-4 sm:px-6">
          <Logo />
          <div className="flex items-center gap-2">
            <button onClick={() => load()} className="flex h-10 w-10 items-center justify-center rounded-xl border border-zinc-200 bg-white hover:bg-zinc-100" aria-label="Обновить" data-testid="admin-refresh-button">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={logout} className="rpm-btn rpm-btn-outline !min-h-[40px] !px-3 text-sm" data-testid="admin-logout-button">
              <LogOut className="h-4 w-4" /> <span className="hidden sm:inline">Выйти</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <h1 className="font-display text-3xl font-bold tracking-tight">Заказы</h1>

        {health && (
          <StripeHealthCard health={health} />
        )}

        {!health && stats?.payment_mode === "test" && (
          <div className="mt-5 rounded-2xl border-[1.5px] border-black bg-[var(--rpm-yellow)] p-4 sm:p-5" data-testid="admin-test-mode-banner">
            <div className="font-display text-base font-bold">🧪 Оплата работает в тестовом режиме — деньги на счёт не приходят</div>
            <p className="mt-1 text-sm text-black">
              Заказы с тестовой картой проходят, но это не настоящие платежи. Чтобы получать деньги: в Emergent откройте <b>Manage → Payments</b>, нажмите «Claim» и подтвердите
              аккаунт Stripe (данные и IBAN для выплат). После подтверждения сайт сам переключится на настоящую оплату.
            </p>
          </div>
        )}

        <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="admin-stats">
          {[
            ["Новые", stats?.new, "bg-[var(--rpm-orange)]"],
            ["Напечатаны", stats?.printed, "bg-[var(--rpm-lime)]"],
            ["Отправлены", stats?.shipped, "bg-black"],
            ["Оплачено всего", stats ? formatEur(stats.revenue) : null, "bg-zinc-300"],
          ].map(([label, val, dot]) => (
            <div key={label} className="rpm-card p-4">
              <div className="flex items-center gap-2 text-xs text-zinc-600">
                <span className={`h-2 w-2 rounded-full ${dot}`} /> {label}
              </div>
              <div className="mt-1 font-display text-2xl font-bold">{val ?? "—"}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 lg:mx-0 lg:px-0" role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={tab === t.id}
                onClick={() => setTab(t.id)}
                className={`h-10 shrink-0 rounded-xl px-4 text-sm font-medium transition-colors ${tab === t.id ? "bg-black text-white" : "border border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-100"}`}
                data-testid={`admin-tab-${t.id}`}
              >
                {t.label}
                {stats && stats[t.id] !== undefined && <span className="ml-1.5 opacity-60">{stats[t.id]}</span>}
              </button>
            ))}
          </div>
          <div className="relative lg:w-72">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск: имя, телефон, текст…" className="rpm-input w-full pl-10 pr-4" data-testid="admin-search-input" />
          </div>
        </div>

        <div className="mt-5 space-y-4" data-testid="admin-orders-list">
          {loading && !orders.length ? (
            <div className="flex items-center justify-center gap-2 py-16 text-zinc-600">
              <Loader2 className="h-5 w-5 animate-spin" /> Загружаем заказы…
            </div>
          ) : filtered.length === 0 ? (
            <div className="rpm-card p-10 text-center" data-testid="admin-orders-empty">
              <p className="font-display text-lg font-semibold">Здесь пока пусто</p>
              <p className="mt-1 text-sm text-zinc-600">{q ? "Ничего не найдено по запросу." : "Новые оплаченные заказы появятся здесь и придут в Telegram."}</p>
            </div>
          ) : (
            filtered.map((o) => <OrderCard key={o.id} o={o} onStatus={setStatus} busy={busy === o.order_number} />)
          )}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    document.title = "Заказы · RPM 3D Print";
    if (!token) {
      setChecked(true);
      return;
    }
    api
      .get("/admin/me", authApi(token))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      })
      .finally(() => setChecked(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  }, []);

  if (!checked) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }
  return token ? <Dashboard token={token} onLogout={logout} /> : <Login onLogin={setToken} />;
}
